// The one place a failed save is turned into something a form can render.
//
// A NOTE ON THE REFERENCES BELOW: errorMessage.vue is the component this
// module replaced, and it was deleted in the Vue 3 migration once nothing
// rendered it any more. Every mention of it here is history — what this module
// was written against and why it behaves the way it does — not a file to go
// and read.
//
// Phase 2 surface 3 is "the forms behind the board... unstyled errors"
// (refresh_plan.md section 4) and web paper cut 8 is "Create Load errors are
// one unstyled run-on sentence". That sentence is built by errorMessage.vue,
// which concatenates every value of `error.data` into one string and shows it
// in an unstyled `alert alert-danger` div. Two things are wrong with it and
// only one of them is styling:
//
//   1. It branches on `status === 422`. This API never answers 422. Every
//      validation refusal in the product is a 400 carrying
//      `error_code: 'REQUEST_VALIDATION_ERROR'` (api/app/Helpers/
//      ValidateRequest.js hands validatorjs's error bag to ApiProblem, which
//      spreads it over the RFC7807 body). So the 422 branch has never run,
//      and every invalid Create Load has always read "The server failed to
//      process the request." — the generic fallback — no matter which field
//      was actually wrong.
//   2. Even when it did run, it dropped the field names on the floor. The API
//      says WHICH field failed; the banner said only that something did.
//
// This module keeps the field names. `fields` is what LwField's `error` prop
// renders next to the control that caused it (with aria-invalid and a
// role="alert" message, which is the field-level validation surface the
// product did not have before phase 1); `message` is what is left over, and
// belongs in the page notice, because a message about a field the form does
// not render must not vanish.
//
// The copy of the two non-validation messages is carried over from
// errorMessage.vue verbatim, deliberately: this is a design refresh, and a
// dispatcher who has learned what "Permission to do this is reserved for
// administrators" means should not have to learn a new sentence for the same
// refusal on the same day the page changes shape.

/** errorMessage.vue's fallback, unchanged. */
export const GENERIC_MESSAGE = 'The server failed to process the request.';

/** errorMessage.vue's 403 copy, unchanged. */
export const FORBIDDEN_MESSAGE =
  'Permission to do this is reserved for administrators. Please contact an administrator for assistance.';

// SEMANTIC CHANGE: there was no message for this case at all. `err.response`
// is undefined when the request never got an answer (the API container is
// down, the browser is offline, CORS refused the preflight), and the old
// pages assigned that undefined straight into `error`, whose `v-if="error"`
// is then false. A save that never reached the server rendered nothing
// whatsoever and left the form looking untouched. It says so now, and it
// says the one thing that distinguishes this case from a refusal: the
// request did not arrive, so nothing was written and retrying is safe.
export const NO_RESPONSE_MESSAGE =
  'The request did not reach the server, so nothing was saved. Check your connection and try again.';

/**
 * The first human-readable string in a validatorjs error entry. The bag's
 * values are arrays of messages per field, but a single string is accepted
 * too rather than assumed against: this shape crosses the wire, and a
 * defensive read here is cheaper than a rendered "[object Object]".
 */
function firstMessage(entry) {
  if (typeof entry === 'string') return entry;
  if (Array.isArray(entry)) {
    const found = entry.find(item => typeof item === 'string' && item.length > 0);
    return found || null;
  }
  return null;
}

// The RFC7807 members ApiProblem.js puts on EVERY problem body. They are
// prose about the response, never about a field the user typed into.
const ENVELOPE_KEYS = new Set(['type', 'title', 'detail', 'status', 'instance', 'error_code']);

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * mapResponseError(response, knownFields) ->
 *   { fields: { [name]: message }, message: string|null }
 *
 * `response` is an axios error's `.response` (or null/undefined when the
 * request never got one). `knownFields` is the list of field names the form
 * actually renders a control for; anything the API complains about that is
 * NOT in that list is folded into `message` instead of being silently
 * dropped, which is the failure mode this whole module exists to stop.
 *
 * `message` is null exactly when every reported problem found a field to
 * live next to. A page that renders both surfaces therefore shows a notice
 * only when there is something the fields cannot say.
 */
export function mapResponseError(response, knownFields = []) {
  if (!response) return { fields: {}, message: NO_RESPONSE_MESSAGE };

  if (response.status === 403) return { fields: {}, message: FORBIDDEN_MESSAGE };

  const data = isPlainObject(response.data) ? response.data : null;
  if (!data) return { fields: {}, message: GENERIC_MESSAGE };

  // The live shape: RFC7807 body with validatorjs's bag spread onto it.
  if (isPlainObject(data.errors)) {
    const known = new Set(knownFields);
    const fields = {};
    const leftovers = [];

    Object.keys(data.errors).forEach(name => {
      const message = firstMessage(data.errors[name]);
      if (!message) return;
      if (known.has(name)) fields[name] = message;
      else leftovers.push(message);
    });

    // Every reported problem was unreadable (an empty bag, or values of a
    // shape firstMessage refuses). Saying nothing would leave the form
    // looking as though the save had worked.
    if (Object.keys(fields).length === 0 && leftovers.length === 0) {
      return { fields: {}, message: GENERIC_MESSAGE };
    }

    return { fields, message: leftovers.length ? leftovers.join(' ') : null };
  }

  // The legacy shape errorMessage.vue was written for: a flat map of
  // field -> message under `data`, on a 422. Kept because dropping a branch
  // while migrating is how a surface quietly loses a behaviour, even one
  // this API has never produced.
  //
  // errorMessage.vue iterated EVERY key of `data` here. This one skips the
  // RFC7807 envelope keys first, because every problem+json body in this API
  // carries `title` and `detail` as prose (ApiProblem.js builds them from a
  // fixed table) and joining those into the banner would show a dispatcher
  // "There was an error validating the request parameters or data." as
  // though it were a complaint about one of their fields. The old code could
  // not hit that because its branch never ran; a branch kept for safety
  // should not be the one that reintroduces the run-on sentence.
  if (response.status === 422) {
    const messages = Object.keys(data)
      .filter(name => !ENVELOPE_KEYS.has(name))
      .map(name => firstMessage(data[name]))
      .filter(Boolean);
    if (messages.length) return { fields: {}, message: messages.join(' ') };
  }

  return { fields: {}, message: GENERIC_MESSAGE };
}
